import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Clock } from 'lucide-react';
import CountUp from '../../components/CountUp';

const problems = [
  { num: '01', title: 'AI answers replaced blue links', desc: "ChatGPT, Gemini, and Perplexity now answer questions directly — without sending users to a search results page. If your client isn't cited, they don't exist." },
  { num: '02', title: "Traditional SEO isn't enough", desc: 'Ranking #1 on Google doesn\'t guarantee appearing in AI answers. AI models pull from different signals — structured data, authority, citations, and entity recognition.' },
  { num: '03', title: 'No one is measuring it', desc: "Most agencies don't track how often their clients appear in AI answers — or how they compare to competitors. What isn't measured can't be improved." }
];

const layers = [
  {
    tag: 'Layer 1 — Organic GEO',
    title: 'Appear in AI recommendations',
    status: { label: 'Available now', tone: 'green' },
    items: ['Schema markup & structured data', 'Google Business Profile optimisation', 'NAP & citation consistency', 'AI engine citation monitoring', 'Local entity optimisation', 'Available now — no media spend']
  },
  {
    tag: 'Layer 2 — Organic AEO',
    title: 'Become the source AI quotes',
    status: { label: 'Available now', tone: 'green' },
    items: ['FAQ & Q&A content strategy', 'Authoritative content creation', 'Entity authority building', 'AI citation source tracking', 'Competitor citation analysis', 'Available now — no media spend']
  },
  {
    tag: 'Layer 3 — Paid ChatGPT Ads',
    title: 'Appear inside ChatGPT responses',
    status: { label: 'Available now', tone: 'green' },
    items: ['Campaign setup & management', 'Contextual & geo targeting', 'Creative & copy optimisation', 'Performance tracking & conversion pixel', 'Live now — managed end-to-end by Marqi (some regulated sectors restricted by OpenAI)']
  }
];

const globalStats = [
  { value: '900M+', title: 'Weekly ChatGPT users worldwide (OpenAI, Feb 2026) — growing every month', desc: 'Available to agencies in every English-speaking market now. Multi-language support expanding throughout 2026.' },
  { value: '195', title: 'Countries where ChatGPT is accessible to consumers', desc: "Your clients' competitors in London, Sydney, Toronto, and Singapore are not yet optimised for AI search. First-mover advantage is real." },
  { value: '3×', title: 'Faster growth in AI search queries vs traditional search — 2025', desc: 'This is not a future trend. Buyers in every market are already asking AI for recommendations. Your clients need to be there.' }
];

const steps = [
  { num: '01', title: 'AI Visibility Audit', desc: "We run a full audit of your client's current AI search presence — how often they appear, in which engines, against which competitors. You get a baseline report to share with the client.", when: 'Day 1–2' },
  { num: '02', title: 'Strategy & Implementation', desc: 'We implement GEO and AEO improvements — schema, citations, content, entity signals. Technical changes go live within the first week. AI visibility gains are tracked monthly — most clients see measurable citation improvements within 60–90 days, depending on current authority and market competitiveness.', when: 'Week 1–2' },
  { num: '03', title: 'Monthly Reporting', desc: 'Every month your client receives a white-label report showing AI Share of Voice, citation frequency, competitor comparison, and improvement vs prior month. Under your brand.', when: 'Ongoing' }
];

const altBg = { background: 'rgba(74, 53, 128, 0.07)' };

const RankOverviewPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rank-bg)' }} data-testid="rank-overview-page">
      <Helmet><title>MarqiRank — AI Search Visibility for Agencies</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="rank-hero-title">
            Your Clients Are Invisible
            <br /><span className="text-[var(--rank-accent)]">In AI Search. Not For Long.</span>
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            900 million people use ChatGPT every week (OpenAI, February 2026). Hundreds of millions more use Gemini, Perplexity,
            and Claude. When they ask for a recommendation — a dentist, a contractor, a financial adviser
            — your clients need to be the answer. MarqiRank is how agencies make that happen.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/audit" className="btn-primary" data-testid="rank-hero-cta-primary">
              Get a Free AI Visibility Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/rank/pricing" className="btn-secondary">View Pricing</Link>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Search Changed. Most Agencies Haven’t.</h2>
            <p className="body-lg">Google is no longer the only place buying decisions happen. Your clients’ customers are asking AI — and getting answers that don’t include your clients.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((p, i) => (
              <div key={p.num} className="feature-card border-l-2 border-l-[var(--rank-primary)] animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-4xl font-bold text-[var(--rank-accent)] mb-4">{p.num}</div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{p.title}</h3>
                <p className="body-md">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Layers */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Three Ways to Rank in AI Search.</h2>
            <p className="body-lg">MarqiRank operates across three layers — two organic, one paid. Most clients start with Layer 1 and Layer 2. Layer 3 — paid ChatGPT Ads — is now live and open to all US businesses, with more markets rolling out.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {layers.map((l, i) => (
              <div key={l.tag} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-[var(--rank-accent)] uppercase tracking-wide">{l.tag}</span>
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{l.title}</h3>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-6 ${l.status.tone === 'green' ? 'bg-[var(--accent-bg)] text-[var(--accent-primary)]' : 'text-[var(--rank-amber)]'}`} style={l.status.tone === 'amber' ? { background: 'rgba(217,144,60,0.15)' } : undefined}>
                  {l.status.label}
                </span>
                <ul className="space-y-3">
                  {l.items.map((it) => (
                    <li key={it} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-[var(--rank-accent)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--text-secondary)] text-sm">{it}</span>
                    </li>
                  ))}
                  {l.pending && (
                    <li className="flex items-start space-x-3">
                      <Clock className="w-5 h-5 text-[var(--rank-amber)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--rank-amber)] text-sm">{l.pending}</span>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global reach */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">AI Search Is a Global Opportunity.</h2>
            <p className="body-lg">ChatGPT, Gemini, and Perplexity are used across every major market. The agencies who establish AI visibility for their clients now — before this becomes a crowded service offering — will own this revenue stream for years.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {globalStats.map((s, i) => (
              <div key={s.title} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <CountUp value={s.value} className="block text-5xl font-bold text-[var(--rank-accent)] mb-4" />
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-3">{s.title}</h3>
                <p className="body-md">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="py-24" style={{ background: 'linear-gradient(135deg, var(--bg-secondary) 0%, rgba(74, 53, 128, 0.22) 100%)' }}>
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="h1 text-[var(--text-primary)] mb-6">Your Clients’ Competitors Are Not Yet Optimised for AI Search.</h2>
              <p className="body-lg">
                Right now, almost none of your clients’ competitors have done anything about AI search
                visibility. That gap is the entire opportunity — and it closes the moment this becomes
                standard practice.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10 mb-12">
              <div>
                <div className="w-10 h-1 bg-[var(--rank-accent)] mb-5"></div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">The pitch is one sentence — that’s the point</h3>
                <blockquote className="border-l-2 border-[var(--rank-primary)] pl-6 italic text-[var(--text-secondary)] mb-4">
                  “We get your business to the top of ChatGPT and Gemini answers — and we prove it monthly.”
                </blockquote>
                <p className="body-md">
                  That’s the entire client conversation. No technical explanation needed, no lengthy proposal.
                  It closes itself because the problem is already obvious to any business owner who’s tried
                  asking AI about their own category.
                </p>
              </div>
              <div>
                <div className="w-10 h-1 bg-[var(--rank-accent)] mb-5"></div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">The agencies who move first own the language</h3>
                <p className="body-md">
                  Whoever explains AI search visibility to a client first becomes the trusted voice on it —
                  even after competitors catch up. Being early here isn’t just a revenue advantage, it’s a
                  positioning advantage that’s hard to unwind.
                </p>
              </div>
            </div>

            <p className="text-center body-md max-w-2xl mx-auto text-[var(--text-secondary)] pt-8 border-t border-[var(--border-subtle)]">
              GEO from $800/mo per client · GEO + AEO from $1,500/mo — no media spend required for either layer.{' '}
              <Link to="/rank/pricing" className="text-[var(--rank-accent)] hover:underline">See full pricing</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16">Up and Running in One Week.</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={s.num} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ background: 'rgba(74,53,128,0.18)' }}>
                  <span className="text-2xl font-bold text-[var(--rank-accent)]">{s.num}</span>
                </div>
                <h3 className="h2 text-[var(--text-primary)] mb-3">{s.title}</h3>
                <p className="body-md mb-6">{s.desc}</p>
                <div className="inline-block px-3 py-1 rounded-full text-sm font-semibold text-[var(--rank-accent)]" style={{ background: 'rgba(74,53,128,0.18)' }}>{s.when}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Your Clients’ Competitors Are Not Yet Optimised for AI Search.</h2>
            <p className="body-lg mb-4 max-w-2xl mx-auto">The agencies who act now will own this channel for their clients. The window is open — but not for long.</p>
            <p className="body-md mb-12 max-w-2xl mx-auto">Book a session and we’ll run a live AI visibility audit for one of your existing clients.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/audit" className="btn-primary glow-effect" data-testid="rank-final-cta-primary">
                Book a Free AI Audit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/rank/pricing" className="btn-secondary">View Pricing</Link>
            </div>
            <p className="body-md mt-10 text-[var(--text-muted)]">
              Want to put clients on TV too?{' '}
              <Link to="/cast" className="text-[var(--rank-accent)] hover:underline">See Marqi Cast</Link>{' '}
              — the other half of how your clients get found.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RankOverviewPage;
